import { Info, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface UsageData {
  plan_count: number
  line_item_count: number
}

interface UsagePopoverProps {
  usage: UsageData | undefined
  isLoading: boolean
  label: string
  onOpen?: () => void
}

export function UsagePopover({
  usage,
  isLoading,
  label,
  onOpen,
}: UsagePopoverProps) {
  return (
    <Popover onOpenChange={open => open && onOpen?.()}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={`Usage info for ${label}`}
        >
          <Info className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" side="left">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading usage...
          </div>
        ) : usage ? (
          <UsageSummary usage={usage} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Unable to load usage info.
          </p>
        )}
      </PopoverContent>
    </Popover>
  )
}

interface UsageSummaryProps {
  usage: UsageData
}

export function UsageSummary({ usage }: UsageSummaryProps) {
  const inUse = usage.plan_count > 0 || usage.line_item_count > 0

  if (!inUse) {
    return (
      <p className="text-sm text-muted-foreground">Not used in any plans.</p>
    )
  }

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-foreground">Currently in use</p>
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">
            {usage.plan_count}
          </span>{' '}
          {usage.plan_count === 1 ? 'plan' : 'plans'}
        </p>
        <p>
          <span className="font-medium text-foreground">
            {usage.line_item_count}
          </span>{' '}
          {usage.line_item_count === 1 ? 'line item' : 'line items'}
        </p>
      </div>
    </div>
  )
}

interface UsageWarningBannerProps {
  usage: UsageData | undefined
  isLoading: boolean
}

export function UsageWarningBanner({
  usage,
  isLoading,
}: UsageWarningBannerProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking usage...
      </div>
    )
  }

  if (!usage) {
    return null
  }

  const inUse = usage.plan_count > 0 || usage.line_item_count > 0

  if (!inUse) {
    return null
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
      <p className="font-medium">This value is currently in use</p>
      <p className="mt-1">
        Changes will affect{' '}
        <span className="font-medium">{usage.plan_count}</span>{' '}
        {usage.plan_count === 1 ? 'plan' : 'plans'} and{' '}
        <span className="font-medium">{usage.line_item_count}</span>{' '}
        {usage.line_item_count === 1 ? 'line item' : 'line items'}.
      </p>
    </div>
  )
}
